import React, { useMemo } from "react";
import { CCard, CCardHeader, CCardBody, CRow, CCol, CListGroup, CListGroupItem } from "@coreui/react";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveLine } from "@nivo/line";
import { useTranslation } from "react-i18next";
import InteractiveTimeline from "../InteractiveTimeline/InteractiveTimeline";
import { formatDuration } from "../../utils/cameraUtils";

const AnalyticsBlock = React.memo(({ title, analyticsData, classIdToMeta, onSegmentDoubleClick, timelineDataOverride, type = "general", operatingMode }) => {
  const { t } = useTranslation();
  
  const pieData = useMemo(() => {
    if (!analyticsData) return [];
    let data = [];
    let totalWorkSeconds = analyticsData.durations_by_people_count?.reduce((sum, item) => sum + item.total_duration_seconds, 0) || 0;
    
    if (analyticsData.total_downtime_seconds > 0) {
      let dtLabel = t("camera.no_people_state");
      if (type === "queue") dtLabel = t("camera.queue_no_people");
      else if (type === "machine") dtLabel = "Простой станка";
      else if (["personnel_control", "cafe", "szsm"].includes(operatingMode)) dtLabel = t("camera.downtime_simple");

      data.push({ id: "Downtime", label: dtLabel, value: analyticsData.total_downtime_seconds, color: "#6c757d" });
    }
    
    if (analyticsData.total_idle_time_seconds > 0) {
      data.push({ id: "Idle", label: t("classes.idle_vehicle"), value: analyticsData.total_idle_time_seconds, color: "#ffc107" });
    }
    
    if (totalWorkSeconds > 0) {
      let label = t("camera.activity_legend_work"), color = "#2eb85c";
      if (type === "chair") { label = t("camera.activity_legend_chair"); color = "#3399ff"; }
      else if (type === "phone") { label = t("camera.activity_legend_phone"); color = "#ff9933"; }
      else if (type === "queue") { label = t("camera.activity_legend_queue", "Очередь"); color = "#9c27b0"; }
      else if (type === "machine") { label = "Работа станка"; color = "#ffb300"; }
      data.push({ id: "Working", label, value: totalWorkSeconds, color });
    }
    return data;
  }, [analyticsData, t, type, operatingMode]);

  const rawTimeline = timelineDataOverride || analyticsData.timeline;
  
  const startDateStr = analyticsData?.start_date || analyticsData?.date_range?.start;
  const endDateStr = analyticsData?.end_date || analyticsData?.date_range?.end;
  const fallbackStart = rawTimeline?.[0]?.start;
  const fallbackEnd = rawTimeline?.[rawTimeline?.length - 1]?.end;

  const timelineDateRange = useMemo(() => {
    if (startDateStr && endDateStr) return { start: startDateStr, end: endDateStr };
    if (fallbackStart && fallbackEnd) return { start: fallbackStart, end: fallbackEnd };
    return null;
  }, [startDateStr, endDateStr, fallbackStart, fallbackEnd]);

  const lineChartData = useMemo(() => {
    if (type !== "queue" || !rawTimeline) return [];
    const shiftToBrowserLocal = (isoString) => {
      if (!isoString) return 0;
      const d = new Date(isoString);
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours() + 3, d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds());
    };
    const dataPoints = [];
    rawTimeline.forEach((seg) => {
      dataPoints.push({ x: shiftToBrowserLocal(seg.start), y: seg.people_count });
      dataPoints.push({ x: shiftToBrowserLocal(seg.end), y: seg.people_count });
    });
    return [{ id: "queue", data: dataPoints }];
  }, [rawTimeline, type]);

  return (
    <CCard className="mb-4 shadow-sm border-top-primary border-top-3">
      <CCardHeader className="bg-white"><strong>{title}</strong></CCardHeader>
      <CCardBody>
        <CRow>
          <CCol md={6}>
            <CListGroup flush>
              <CListGroupItem>
                <strong>
                  {type === "queue" ? t("camera.queue_no_people") : type === "machine" ? "Простой станка" : ["personnel_control", "cafe", "szsm"].includes(operatingMode) ? t("camera.downtime_simple") : t("camera.total_downtime")}:
                </strong> {formatDuration(analyticsData.total_downtime_seconds)}
              </CListGroupItem>
              
              {analyticsData.total_idle_time_seconds > 0 && <CListGroupItem><strong>{t("camera.total_idle")}:</strong> {formatDuration(analyticsData.total_idle_time_seconds)}</CListGroupItem>}
              
              <CListGroupItem>
                <strong>
                  {type === "chair" ? t("camera.activity_chair_title") : type === "phone" ? t("camera.activity_phone_title") : type === "queue" ? t("camera.activity_queue_title", "Активность: Очередь") : type === "machine" ? "Время работы станка" : t("camera.work_time")}:
                </strong> {formatDuration(analyticsData.durations_by_people_count?.reduce((sum, item) => sum + item.total_duration_seconds, 0) || 0)}
              </CListGroupItem>
              
              {/* СКРЫВАЕМ СТРОКУ МАКСИМАЛЬНЫХ ЛЮДЕЙ ДЛЯ СТАНКА */}
              {type !== "machine" && (
                  <CListGroupItem>
                      <strong>{type === "queue" ? t("camera.queue_max_people") : t("camera.max_people")}:</strong> {analyticsData.max_concurrent_people}
                  </CListGroupItem>
              )}

              {analyticsData.durations_by_people_count?.length > 0 && type !== "machine" && (
                <div className="mt-2 pt-2 border-top">
                  <small className="text-muted d-block mb-1">{t("camera.detail_people_count")}</small>
                  {analyticsData.durations_by_people_count.map((item) => {
                    let metaId;
                    if (type === "general") metaId = -100 - item.people_count;
                    else if (type === "chair") metaId = -300 - item.people_count;
                    else if (type === "phone") metaId = -400 - item.people_count;
                    else if (type === "queue") metaId = -500 - item.people_count;
                    if (item.people_count >= 10) metaId = metaId - 1;
                    const meta = classIdToMeta[String(metaId)] || classIdToMeta["default"];
                    return (
                      <div key={item.people_count} className="d-flex justify-content-between small px-2 align-items-center">
                        <span><span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: meta?.color || "#ccc", marginRight: "6px" }}></span>{item.people_count} чел:</span>
                        <span>{formatDuration(item.total_duration_seconds)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CListGroup>
          </CCol>
          <CCol md={6}>
            {pieData.length > 0 ? (
              <div style={{ height: "200px" }}>
                <ResponsivePie data={pieData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }} innerRadius={0.6} padAngle={1} cornerRadius={3} activeOuterRadiusOffset={8} colors={{ datum: "data.color" }} enableArcLabels={true} arcLabel={(d) => `${Math.round((d.value / pieData.reduce((acc, v) => acc + v.value, 0)) * 100)}%`} enableArcLinkLabels={false} tooltip={({ datum }) => (<div style={{ padding: "5px 10px", background: "white", border: "1px solid #ccc", borderRadius: "4px", fontSize: "12px" }}><div style={{ width: "10px", height: "10px", background: datum.color, display: "inline-block", marginRight: "5px" }}></div><strong>{datum.label}</strong>: {formatDuration(datum.value)}</div>)} />
              </div>
            ) : (<p className="text-muted text-center m-0 pt-5">{t("camera.no_videos")}</p>)}
          </CCol>
        </CRow>
        <h6 className="mt-4 mb-2 text-primary">{t("camera.timeline_local")}</h6>
        {rawTimeline ? <InteractiveTimeline timelineData={rawTimeline} dateRange={timelineDateRange} onItemDoubleClick={onSegmentDoubleClick} classIdToMeta={classIdToMeta} /> : <p className="text-muted small">Нет данных для временной шкалы.</p>}
        {type === "queue" && lineChartData[0]?.data.length > 0 && (
          <div className="mt-4 border-top pt-4">
            <h6 className="mb-2 text-primary">{t("camera.queue_chart_title")}</h6>
            <div style={{ height: "300px" }}><ResponsiveLine data={lineChartData} margin={{ top: 20, right: 20, bottom: 50, left: 50 }} xScale={{ type: "time", format: "native" }} yScale={{ type: "linear", min: 0, max: "auto" }} curve="stepAfter" useMesh={true} colors={["#9c27b0"]} enableArea={true} areaOpacity={0.1} /></div>
          </div>
        )}
      </CCardBody>
    </CCard>
  );
});
export default AnalyticsBlock;